import { FIXED_LIMITS, type ChannelDto, type ForumTagDto } from '@lagerfeuer/shared';
import { Plus, Trash2, TriangleAlert } from 'lucide-react';
import { useCallback, useMemo, useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { EmojiField } from '../../components/EmojiField';
import { SaveBar, SettingsHeader, SettingsLayout, type SettingsSection } from '../../components/SettingsLayout';
import { errorMessage } from '../../lib/api';
import { useDocumentTitle } from '../../lib/documentTitle';
import { channelsApi, type ChannelPatch } from '../../lib/endpoints';
import { paths } from '../../lib/routes';
import { useAppStore } from '../../store/app';
import { can, channelPermissions, serverPermissions } from '../../store/selectors';
import { toast } from '../../store/ui';
import { InviteList } from '../server-settings/InvitesTab';
import { useEdits } from '../server-settings/useEdits';
import { CHANNEL_TYPE_LABELS } from '../shell/channelIcons';
import { normalizeChannelName, SLOWMODES } from './channelHelpers';
import { OverwritesEditor } from './OverwritesEditor';
import '../server-settings/server-settings.css';

const dispatch = useAppStore.getState().dispatch;

/** Kanaleinstellungen `/channel-settings/:channelId/:tab`: Übersicht, Berechtigungen, Einladungen, Löschen. */
export function ChannelSettings() {
  const { channelId = '', tab } = useParams();
  const navigate = useNavigate();
  const channel = useAppStore((s) => s.channels[channelId]);
  const roles = useAppStore((s) => (channel?.serverId ? s.roles[channel.serverId] : undefined));
  const servers = useAppStore((s) => s.servers);

  const sections = useMemo<SettingsSection[]>(() => {
    if (!channel?.serverId) return [];
    const st = useAppStore.getState();
    const cp = channelPermissions(st, channel.id);
    const sp = serverPermissions(st, channel.serverId);
    const list: SettingsSection[] = [];
    if (can(cp, 'MANAGE_CHANNELS')) list.push({ id: 'uebersicht', label: 'Übersicht' });
    if (can(cp, 'MANAGE_ROLES')) list.push({ id: 'berechtigungen', label: 'Berechtigungen' });
    if (channel.type !== 'CATEGORY' && can(sp, 'MANAGE_SERVER')) list.push({ id: 'einladungen', label: 'Einladungen' });
    if (can(cp, 'MANAGE_CHANNELS')) list.push({ id: 'loeschen', label: channel.type === 'CATEGORY' ? 'Kategorie löschen' : 'Kanal löschen', danger: true });
    return list.map((s) => ({ ...s, testId: `channel-settings-tab-${s.id}` }));
  }, [channel, roles, servers]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = useCallback(() => {
    if (!channel?.serverId) return void navigate(paths.home());
    void navigate(channel.type === 'CATEGORY' || channel.type === 'VOICE' ? paths.server(channel.serverId) : paths.channel(channel.serverId, channel.id));
  }, [channel, navigate]);
  const select = useCallback((id: string) => void navigate(paths.channelSettings(channelId, id), { replace: true }), [navigate, channelId]);

  useDocumentTitle(channel ? `Kanaleinstellungen – ${channel.name ?? ''}` : null);

  if (!channel?.serverId) return <Navigate to={paths.home()} replace />;
  const active = sections.find((s) => s.id === tab) ?? sections[0];
  const title = `${channel.type === 'CATEGORY' ? '' : channel.type === 'VOICE' ? '🔊 ' : '# '}${channel.name ?? ''}`;
  if (!active) {
    return (
      <SettingsLayout title={title} sections={[]} active="" onSelect={select} onClose={close} testId="channel-settings">
        <p className="muted" data-testid="channel-settings-forbidden">
          Du hast keine Rechte, diesen Kanal zu bearbeiten.
        </p>
      </SettingsLayout>
    );
  }
  if (tab !== active.id) return <Navigate to={paths.channelSettings(channelId, active.id)} replace />;
  return (
    <SettingsLayout title={title} sections={sections} active={active.id} onSelect={select} onClose={close} testId="channel-settings">
      <div data-testid={`channel-settings-panel-${active.id}`}>
        {active.id === 'uebersicht' && <ChannelOverview key={channel.id} channel={channel} />}
        {active.id === 'berechtigungen' && (
          <div className="stack settings-stack">
            <SettingsHeader title="Berechtigungen">
              Überschreibe Rechte je Rolle oder Mitglied. „Neutral" übernimmt die Serverrechte; Verweigern einer Rolle wird durch Erlauben bei einer anderen Rolle des Mitglieds aufgehoben,
              Mitglieds-Overrides gewinnen immer.
            </SettingsHeader>
            <OverwritesEditor key={channel.id} channel={channel} />
          </div>
        )}
        {active.id === 'einladungen' && (
          <div className="stack settings-stack">
            <SettingsHeader title="Einladungen">Aktive Einladungen, die in diesen Kanal führen.</SettingsHeader>
            <InviteList serverId={channel.serverId} channelId={channel.id} />
          </div>
        )}
        {active.id === 'loeschen' && <DeleteChannel channel={channel} />}
      </div>
    </SettingsLayout>
  );
}

interface EditableChannel {
  name: string;
  topic: string | null;
  nsfw: boolean;
  slowmodeSeconds: number;
  bitrate: number;
  userLimit: number;
  parentId: string | null;
  defaultReactionEmoji: string | null;
}

function ChannelOverview({ channel }: { channel: ChannelDto }) {
  const limits = useAppStore((s) => s.limits);
  const channels = useAppStore((s) => s.channels);
  const categories = useMemo(() => Object.values(channels).filter((c) => c.serverId === channel.serverId && c.type === 'CATEGORY').sort((a, b) => a.position - b.position), [channels, channel.serverId]);
  const base = useMemo<EditableChannel>(
    () => ({
      name: channel.name ?? '',
      topic: channel.topic,
      nsfw: channel.nsfw,
      slowmodeSeconds: channel.slowmodeSeconds,
      bitrate: channel.bitrate ?? 64000,
      userLimit: channel.userLimit ?? 0,
      parentId: channel.parentId,
      defaultReactionEmoji: channel.defaultReactionEmoji ?? null,
    }),
    [channel],
  );
  const { value, patch, dirty, set, reset } = useEdits(base);
  const [busy, setBusy] = useState(false);
  if (!value) return null;
  const t = channel.type;
  const textLike = t === 'TEXT' || t === 'ANNOUNCEMENT' || t === 'FORUM';
  const maxBitrate = Math.max(8000, limits.maxAudioBitrate);
  const maxUsers = limits.maxVoiceUsers > 0 ? Math.min(99, limits.maxVoiceUsers) : 99;

  const save = async () => {
    const body: ChannelPatch = {};
    if (patch.name !== undefined) {
      const n = normalizeChannelName(patch.name.trim(), t);
      if (!n) return toast.error('Der Name darf nicht leer sein.');
      body.name = n;
    }
    if (patch.topic !== undefined) body.topic = patch.topic?.trim() || null;
    if (patch.nsfw !== undefined) body.nsfw = patch.nsfw;
    if (patch.slowmodeSeconds !== undefined) body.slowmodeSeconds = patch.slowmodeSeconds;
    if (patch.bitrate !== undefined) body.bitrate = patch.bitrate;
    if (patch.userLimit !== undefined) body.userLimit = patch.userLimit;
    if (patch.parentId !== undefined) body.parentId = patch.parentId;
    if (patch.defaultReactionEmoji !== undefined) body.defaultReactionEmoji = patch.defaultReactionEmoji?.trim() || null;
    setBusy(true);
    try {
      dispatch('CHANNEL_UPDATE', await channelsApi.update(channel.id, body));
      reset();
      toast.success('Gespeichert');
    } catch (e) {
      toast.error('Speichern fehlgeschlagen', errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack settings-stack">
      <SettingsHeader title="Übersicht">{CHANNEL_TYPE_LABELS[t]}</SettingsHeader>
      <div className="field">
        <label htmlFor="channel-name">{t === 'CATEGORY' ? 'Kategoriename' : 'Kanalname'}</label>
        <input id="channel-name" className="input" value={value.name} maxLength={FIXED_LIMITS.channelNameMax} onChange={(e) => set('name', e.target.value)} data-testid="channel-name" />
      </div>
      {textLike && (
        <div className="field">
          <label htmlFor="channel-topic">{t === 'FORUM' ? 'Richtlinien für Beiträge' : 'Kanalthema'}</label>
          <textarea
            id="channel-topic"
            className="textarea"
            rows={3}
            maxLength={FIXED_LIMITS.channelTopicMax}
            value={value.topic ?? ''}
            onChange={(e) => set('topic', e.target.value || null)}
            data-testid="channel-topic"
          />
          <span className="field-hint">
            {(value.topic ?? '').length} / {FIXED_LIMITS.channelTopicMax}
          </span>
        </div>
      )}
      {t !== 'CATEGORY' && (
        <div className="field">
          <label htmlFor="channel-parent">Kategorie</label>
          <select id="channel-parent" className="select" value={value.parentId ?? ''} onChange={(e) => set('parentId', e.target.value || null)} data-testid="channel-parent">
            <option value="">Keine Kategorie</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      )}
      {(textLike || t === 'VOICE') && (
        <div className="field">
          <label htmlFor="channel-slowmode">Slowmode</label>
          <select id="channel-slowmode" className="select" value={value.slowmodeSeconds} onChange={(e) => set('slowmodeSeconds', Number(e.target.value))} data-testid="channel-slowmode">
            {SLOWMODES.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <span className="field-hint">Mitglieder können nur einmal pro Intervall schreiben (außer mit „Nachrichten verwalten").</span>
        </div>
      )}
      {t !== 'CATEGORY' && (
        <label className="switch-row">
          <input type="checkbox" className="switch" checked={value.nsfw} onChange={(e) => set('nsfw', e.target.checked)} data-testid="channel-nsfw" />
          <span>
            Altersbeschränkter Kanal (NSFW)
            <span className="field-hint" style={{ display: 'block' }}>
              Vor dem Öffnen wird nach dem Alter gefragt.
            </span>
          </span>
        </label>
      )}
      {t === 'VOICE' && (
        <>
          <div className="field">
            <label htmlFor="channel-bitrate">Bitrate: {Math.round(value.bitrate / 1000)} kbit/s</label>
            <input
              id="channel-bitrate"
              type="range"
              min={8000}
              max={maxBitrate}
              step={1000}
              value={Math.min(value.bitrate, maxBitrate)}
              onChange={(e) => set('bitrate', Number(e.target.value))}
              data-testid="channel-bitrate"
            />
            <span className="field-hint">Höchstens {Math.round(maxBitrate / 1000)} kbit/s (Plattform-Limit). Mehr Bitrate = bessere Qualität, mehr Bandbreite.</span>
          </div>
          <div className="field">
            <label htmlFor="channel-user-limit">Nutzerlimit: {value.userLimit === 0 ? 'unbegrenzt' : `${value.userLimit} Personen`}</label>
            <input id="channel-user-limit" type="range" min={0} max={maxUsers} step={1} value={value.userLimit} onChange={(e) => set('userLimit', Number(e.target.value))} data-testid="channel-user-limit" />
          </div>
        </>
      )}
      {t === 'FORUM' && (
        <>
          <div className="field">
            <label htmlFor="channel-default-reaction">Standard-Reaktion für Beiträge</label>
            <EmojiField
              id="channel-default-reaction"
              value={value.defaultReactionEmoji}
              onChange={(v) => set('defaultReactionEmoji', v)}
              label="Standard-Reaktion"
              serverId={channel.serverId}
              format="reactionKey"
              testId="channel-default-reaction"
            />
            <span className="field-hint">Wird bei jedem Beitrag als Reaktionsknopf angeboten.</span>
          </div>
          <ForumTags channel={channel} />
        </>
      )}
      <SaveBar dirty={dirty} busy={busy} onSave={() => void save()} onReset={reset} testId="channel-save" />
    </div>
  );
}

function updateTags(channel: ChannelDto, fn: (tags: ForumTagDto[]) => ForumTagDto[]): void {
  const cur = useAppStore.getState().channels[channel.id] ?? channel;
  dispatch('CHANNEL_UPDATE', { ...cur, availableTags: fn(cur.availableTags ?? []) });
}

/** Forum-Tags anlegen, umbenennen, moderieren (nur Mods vergeben), löschen. */
function ForumTags({ channel }: { channel: ChannelDto }) {
  const tags = [...(channel.availableTags ?? [])].sort((a, b) => a.position - b.position);
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('');
  const [busy, setBusy] = useState(false);
  const full = tags.length >= FIXED_LIMITS.forumTagsMax;

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || full) return;
    setBusy(true);
    try {
      const tag = await channelsApi.createTag(channel.id, { name: name.trim(), emoji: emoji.trim() || null });
      updateTags(channel, (l) => (l.some((x) => x.id === tag.id) ? l : [...l, tag]));
      setName('');
      setEmoji('');
    } catch (err) {
      toast.error('Tag konnte nicht angelegt werden', errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const patchTag = async (tag: ForumTagDto, body: { name?: string; emoji?: string | null; moderated?: boolean }) => {
    try {
      const res = await channelsApi.updateTag(channel.id, tag.id, body);
      updateTags(channel, (l) => l.map((x) => (x.id === res.id ? res : x)));
    } catch (err) {
      toast.error('Tag konnte nicht geändert werden', errorMessage(err));
    }
  };

  const removeTag = async (tag: ForumTagDto) => {
    try {
      await channelsApi.deleteTag(channel.id, tag.id);
      updateTags(channel, (l) => l.filter((x) => x.id !== tag.id));
    } catch (err) {
      toast.error('Tag konnte nicht gelöscht werden', errorMessage(err));
    }
  };

  return (
    <section className="stack" style={{ gap: '0.5rem' }} data-testid="forum-tags">
      <h2 className="settings-subheading">
        Tags ({tags.length}/{FIXED_LIMITS.forumTagsMax})
      </h2>
      <p className="field-hint" style={{ margin: 0 }}>
        Beiträge können mit Tags versehen werden. Moderierte Tags dürfen nur Mitglieder mit „Threads verwalten" setzen. Änderungen an Tags werden sofort gespeichert.
      </p>
      {tags.length > 0 && (
        <ul className="settings-list">
          {tags.map((tag) => (
            <li key={tag.id} className="settings-row" data-testid="forum-tag" data-tag-id={tag.id}>
              <EmojiField
                value={tag.emoji}
                onChange={(v) => v !== tag.emoji && void patchTag(tag, { emoji: v })}
                label={`Emoji von ${tag.name}`}
                serverId={channel.serverId}
                testId="forum-tag-emoji"
              />
              <input
                className="input input-inline"
                aria-label={`Name von ${tag.name}`}
                defaultValue={tag.name}
                maxLength={20}
                onBlur={(e) => e.target.value.trim() && e.target.value.trim() !== tag.name && void patchTag(tag, { name: e.target.value.trim() })}
                data-testid="forum-tag-name"
              />
              <label className="switch-row small">
                <input type="checkbox" className="switch" checked={tag.moderated} onChange={(e) => void patchTag(tag, { moderated: e.target.checked })} data-testid="forum-tag-moderated" />
                moderiert
              </label>
              <button className="icon-btn" aria-label={`Tag ${tag.name} löschen`} onClick={() => void removeTag(tag)} data-testid="forum-tag-delete">
                <Trash2 size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <form className="row" onSubmit={add} style={{ flexWrap: 'wrap' }}>
        <EmojiField value={emoji || null} onChange={(v) => setEmoji(v ?? '')} label="Emoji des neuen Tags" serverId={channel.serverId} testId="forum-tag-new-emoji" />
        <input className="input" style={{ flex: 1, minWidth: 140 }} placeholder="Neuer Tag" aria-label="Name des neuen Tags" maxLength={20} value={name} onChange={(e) => setName(e.target.value)} data-testid="forum-tag-new-name" />
        <button className="btn" disabled={busy || full || !name.trim()} data-testid="forum-tag-add">
          <Plus size={16} /> Tag hinzufügen
        </button>
      </form>
    </section>
  );
}

function DeleteChannel({ channel }: { channel: ChannelDto }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isCat = channel.type === 'CATEGORY';
  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await channelsApi.remove(channel.id);
      void navigate(paths.server(channel.serverId!), { replace: true });
      dispatch('CHANNEL_DELETE', { id: channel.id, serverId: channel.serverId, parentId: channel.parentId });
      toast.success(isCat ? 'Kategorie gelöscht' : 'Kanal gelöscht');
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };
  return (
    <div className="stack settings-stack">
      <SettingsHeader title={isCat ? 'Kategorie löschen' : 'Kanal löschen'} />
      <p className="notice danger" role="note">
        <TriangleAlert size={18} aria-hidden="true" />
        {isCat
          ? `Die Kategorie „${channel.name}" wird gelöscht. Die enthaltenen Kanäle bleiben erhalten und landen ohne Kategorie oben in der Liste.`
          : `„${channel.name}" und alle Nachrichten, Threads und Anhänge darin werden endgültig gelöscht.`}
      </p>
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      <div>
        <button className="btn btn-danger" onClick={() => void remove()} disabled={busy} data-testid="channel-delete-confirm">
          {busy ? 'Wird gelöscht …' : isCat ? 'Kategorie endgültig löschen' : 'Kanal endgültig löschen'}
        </button>
      </div>
    </div>
  );
}
